/**
 * Revisão por IA dentro do texto, sem painel (docs/PLANO_EDITOR_AUTOS.md, Bloco 4b):
 * o cartão de uma sugestão (no balão junto do trecho; no celular, fixo no pé) e a
 * barra da revisão acima da barra de status. Como no Word: o editor não perde
 * largura e não depende de nada ao lado dele.
 */
import { forwardRef, type KeyboardEvent } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { dicaCreditosDaRevisao } from '@/lib/ia/creditos';
import type { Resolucao, SugestaoIA } from '../extensoes/revisao';
import { Botao } from './comum';

export type SugestaoComResolucao = SugestaoIA & { resolvida?: Resolucao };

const ROTULO_RESOLUCAO: Record<Resolucao, string> = {
  aceita: 'Aceita.',
  recusada: 'Recusada.',
  obsoleta: 'O trecho mudou depois da revisão; a sugestão não vale mais.',
};

/**
 * Põe o foco do teclado no cartão: no Aceitar, ou no fechar quando não há o que
 * aceitar (sugestão que caducou; sem isso o foco caía no `<body>`).
 */
export function focarCartao(cartao: HTMLElement | null) {
  cartao?.querySelector<HTMLElement>('[data-acao="aceitar"], [data-acao="fechar"]')?.focus({ preventScroll: true });
}

/**
 * O que a IA sugere para um trecho. Enter aceita e Shift+Enter recusa (os dois vão
 * à próxima); Esc fecha. Enter num botão é o daquele botão: no Recusar, recusa.
 */
export const CartaoSugestaoIA = forwardRef<HTMLDivElement, {
  sugestao: SugestaoComResolucao;
  somenteLeitura: boolean;
  aoAceitar: () => void; aoRecusar: () => void; aoFechar: () => void;
}>(function CartaoSugestaoIA({ sugestao: s, somenteLeitura, aoAceitar, aoRecusar, aoFechar }, ref) {
  const teclas = (e: KeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); aoFechar(); return; }
    if (e.key !== 'Enter' || somenteLeitura || s.resolvida) return;
    const acao = (e.target as HTMLElement).closest?.('[data-acao]')?.getAttribute('data-acao');
    if (acao === 'fechar') return;
    e.preventDefault();
    if (e.shiftKey || acao === 'recusar') aoRecusar(); else aoAceitar();
  };
  return (
    <div ref={ref} className="cm-ef-cartao-ia" onKeyDown={teclas}>
      <div className="cm-ef-ia-cab">
        <span className="cm-ef-ia-tipo">{s.tipo}</span>
        <Botao className="cm-ef-ia-fechar" data-acao="fechar" dica={{ nome: 'Fechar', atalho: 'Esc' }}
          icone={<X className="cm-ef-ico" aria-hidden />} onClick={aoFechar} />
      </div>
      <span className="cm-ef-ia-de">{s.de}</span>
      <span className="cm-ef-ia-para">{s.para}</span>
      {s.motivo && <span className="cm-ef-ia-porque">{s.motivo}</span>}
      {s.resolvida ? (
        <span className="cm-ef-ia-porque">{ROTULO_RESOLUCAO[s.resolvida]}</span>
      ) : !somenteLeitura && (
        <div className="cm-ef-linha-bt">
          <Button type="button" size="sm" data-acao="aceitar" aria-keyshortcuts="Enter" onClick={aoAceitar}>Aceitar</Button>
          <Button type="button" size="sm" variant="outline" data-acao="recusar" aria-keyshortcuts="Shift+Enter" onClick={aoRecusar}>Recusar</Button>
        </div>
      )}
    </div>
  );
});

/** A revisão em curso: andamento, navegação entre as sugestões e as ações em lote. */
export function BarraRevisaoIA({ estado, erro, sugestoes, foco, perdidas, creditos, somenteLeitura, aoAnterior, aoProxima, aoAceitarTodas, aoRevisar, aoFechar }: {
  estado: 'pronto' | 'revisando' | 'erro'; erro: string;
  /** Na ordem do texto. */
  sugestoes: SugestaoComResolucao[]; foco: string | null;
  /** As que voltaram da IA mas não acharam o trecho no texto. */
  perdidas: number;
  creditos: number | null; somenteLeitura: boolean;
  aoAnterior: () => void; aoProxima: () => void; aoAceitarTodas: () => void; aoRevisar: () => void; aoFechar: () => void;
}) {
  const pendentes = sugestoes.filter((s) => !s.resolvida);
  const posicao = foco ? pendentes.findIndex((s) => s.id === foco) : -1;
  const conta = (como: Resolucao) => sugestoes.filter((s) => s.resolvida === como).length;
  const revisarDeNovo = !somenteLeitura && (
    <Botao dica={{ nome: 'Revisar de novo', desc: creditos !== null ? `Revisa o texto como está agora. ${dicaCreditosDaRevisao(creditos)}` : 'Revisa o texto como está agora.' }}
      rotulo="Revisar de novo" onClick={aoRevisar} />
  );

  let corpo;
  if (estado === 'revisando') {
    corpo = <span className="cm-ef-rev-txt"><Spinner size={16} label="Revisando a peça" /> Revisando a peça…</span>;
  } else if (estado === 'erro') {
    corpo = (
      <>
        <span className="cm-ef-rev-txt cm-ef-erro-txt">{erro}</span>
        {!somenteLeitura && <Botao dica={{ nome: 'Tentar de novo' }} rotulo="Tentar de novo" onClick={aoRevisar} />}
      </>
    );
  } else if (!sugestoes.length) {
    corpo = (
      <>
        <span className="cm-ef-rev-txt">
          {perdidas ? 'O texto mudou enquanto a IA lia: nenhuma sugestão pôde ser marcada.' : 'A revisão não encontrou o que sugerir.'}
        </span>
        {revisarDeNovo}
      </>
    );
  } else if (pendentes.length) {
    corpo = (
      <>
        <span className="cm-ef-rev-txt">
          <b>Revisão:</b> {posicao >= 0 ? `${posicao + 1} de ${pendentes.length}` : `${pendentes.length} ${pendentes.length === 1 ? 'sugestão' : 'sugestões'}`}
          {perdidas > 0 && <span className="cm-ef-rev-det"> · {perdidas === 1 ? '1 não pôde ser marcada' : `${perdidas} não puderam ser marcadas`} (o texto mudou)</span>}
        </span>
        <span className="cm-ef-rev-nav" role="group" aria-label="Navegar pelas sugestões">
          <Botao dica={{ nome: 'Sugestão anterior' }} icone={<ChevronLeft className="cm-ef-ico" aria-hidden />} onClick={aoAnterior} />
          <Botao dica={{ nome: 'Próxima sugestão' }} icone={<ChevronRight className="cm-ef-ico" aria-hidden />} onClick={aoProxima} />
        </span>
        {!somenteLeitura && pendentes.length > 1 && <Botao dica={{ nome: 'Aceitar todas', desc: 'Aceita as sugestões pendentes. Ctrl+Z desfaz.' }} rotulo={`Aceitar todas (${pendentes.length})`} onClick={aoAceitarTodas} />}
        {revisarDeNovo}
      </>
    );
  } else {
    const obsoletas = conta('obsoleta');
    corpo = (
      <>
        <span className="cm-ef-rev-txt">
          <b>Revisão concluída:</b> {conta('aceita')} {conta('aceita') === 1 ? 'aceita' : 'aceitas'}, {conta('recusada')} {conta('recusada') === 1 ? 'recusada' : 'recusadas'}
          {obsoletas > 0 && `, ${obsoletas} sem efeito (o trecho mudou)`}.
        </span>
        {revisarDeNovo}
      </>
    );
  }

  return (
    <div className="cm-ef-rev-barra" role="region" aria-label="Revisão com IA" aria-live="polite">
      {corpo}
      <Botao className="cm-ef-rev-fechar"
        dica={{ nome: 'Encerrar a revisão', desc: pendentes.length && estado === 'pronto' ? 'As sugestões pendentes somem do texto.' : undefined }}
        icone={<X className="cm-ef-ico" aria-hidden />} onClick={aoFechar} />
    </div>
  );
}
